import { environment } from "../../environments/environment";
import type { Agent } from "../AgentListView";
export const base = environment.baseURL;

/** Raw agent shape from GET /ai-agent/app/:appId/list */
export interface ApiAgent {
  agentUUID: string;
  aiAgentPublicApiUrl?: string;
  appId?: string;
  createdTime?: string;
  description?: string;
  flow?: object | null;
  iconUrl?: string;
  id: string;
  instructions?: string;
  lastUpdatedTime?: string;
  name: string;
  settings?: string;
  subAgents?: object | null;
  tags?: string | string[] | null;
  tenantId?: string;
  tools?: string;
  triggers?: object | null;
  misc?: string;
}

/**
 * Registers/links the Teams channel with the AI agent backend.
 * Called after authToken is received from /teams/authorize.
 */
export async function registerTeamsChannel(
  tenantId: string,
  channelId: string,
  authToken: string,
  teamId?: string,
  envName?: string
): Promise<unknown> {
  const query = new URLSearchParams();
  if (teamId) query.set("teamId", teamId);
  query.set("env", envName || environment.envName || "sandbox");
  const path = `ai-agent/tenant/${tenantId}/channels/${encodeURIComponent(channelId)}?${query.toString()}`;
  return doFetch(path, {
    method: "GET",
    token: `Bearer ${authToken}`,
  });
}

/**
 * Fetches agents for an app from the AI Agent list API.
 * @param appId - App ID (e.g. "2")
 * @param token - JWT or auth token for the request
 * @param teamId - Optional Teams team id to send as a query param
 * @param envName - Optional environment name to send as a query param
 */
export async function fetchAgents(
  appId: string,
  token: string,
  teamId?: string,
  envName?: string
): Promise<ApiAgent[]> {
  const query = new URLSearchParams();
  if (teamId) query.set("teamId", teamId);
  if (envName) query.set("env", envName);
  const queryString = query.toString();
  const path = queryString
    ? `ai-agent/app/${appId}/list?${queryString}`
    : `ai-agent/app/${appId}/list`;
  const result = await doFetch(path, {
    method: "GET",
    token: `Bearer ${token}`,
  });
  if (result == null) return [];
  if (Array.isArray(result)) {
    const flat = result.flat();
    return flat as ApiAgent[];
  }
  if (typeof result === "object" && result !== null) {
    const values = Object.values(result);
    const flat = values.flat();
    return flat as ApiAgent[];
  }
  return [];
}

/** Maps API agent to the UI Agent type (for AgentListView / AgentMonitoringView) */
export function mapApiAgentToAgent(api: ApiAgent): Agent {
  let tags: string[] = [];
  if (typeof api.tags === "string") {
    try {
      const parsed = JSON.parse(api.tags);
      tags = Array.isArray(parsed) ? parsed : [];
    } catch {
      tags = [];
    }
  } else if (Array.isArray(api.tags)) {
    tags = api.tags;
  }
  return {
    id: api.id,
    agentUUID: api.agentUUID ?? api.id,
    aiAgentPublicApiUrl: api.aiAgentPublicApiUrl,
    appId: api.appId,
    createdTime: api.createdTime,
    description: api.description ?? "",
    flow: api.flow,
    iconUrl: api.iconUrl,
    instructions: api.instructions,
    lastUpdatedTime: api.lastUpdatedTime,
    name: api.name ?? "",
    settings: api.settings,
    subAgents: api.subAgents,
    tags: tags.length > 0 ? tags : undefined,
    tenantId: api.tenantId,
    tools: api.tools,
    triggers: api.triggers,
    misc: api.misc,
  };
}

export const doFetch = async (path: string, options: any, headers?: any) => {
  // console.log('options', options);
  const url = base + `/${path}`;

  const payload: any = {
    method: options?.method,
    headers: {
      ...(headers && headers),
      'Content-Type': 'application/json',
    }
  };

  // Add abort signal if provided
  if (options?.signal) {
    payload.signal = options.signal;
  }

  if (options?.tokenType === "JWE") {
    payload.headers = {
      ...(headers && headers),
      'Content-Type': 'application/json',
      'Authorization': `JWE ${options?.token || ''}`,
    };
  } else if (options?.token) {
    payload.headers = {
      ...(headers && headers),
      'Content-Type': 'application/json',
      'Authorization': options.token,
    };
  } else {
    payload.headers = {
      ...(headers && headers),
      'Content-Type': 'application/json',
    };
  }

  if (options?.body) payload.body = JSON.stringify(options?.body);
  if (options?.form) {
    payload.headers = {
      ...(headers && headers),
      'Authorization': options?.token || '',
    };
    payload.body = options?.form;
  }

  const hasAuthHeader = Boolean(payload.headers?.Authorization);
  if (!options?.skipAuth && !hasAuthHeader) {
    console.warn('doFetch aborted: missing authorization headers');
    return null;
  }

  try {
    const response = await fetch(url, payload);

    if (!response.ok) {
      console.error(`HTTP error! status: ${response.status}`);
      return null;
    }

    return await response.json();

  } catch (error) {
    console.error('Error during reset:', error);
    throw error; // Re-throw to allow caller to handle abort errors
  }
};
